<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-horizontal-padding orangehrm-vertical-padding">
    <oxd-text tag="h6" class="orangehrm-main-title">
      {{ $t('pim.edit_dependent') }}
    </oxd-text>
    <oxd-divider />
    <oxd-form :loading="isLoading" @submit-valid="onSave">
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="dependent.name"
              :label="$t('general.name')"
              :rules="rules.name"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="dependent.relationshipType"
              type="select"
              :label="$t('pim.relationship')"
              :rules="rules.relationshipType"
              :options="relationshipOptions"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item v-if="showRelationship">
            <oxd-input-field
              v-model="dependent.relationship"
              :label="$t('pim.please_specify')"
              :rules="rules.relationship"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <date-input
              v-model="dependent.dateOfBirth"
              :label="$t('pim.date_of_birth')"
              :rules="rules.dateOfBirth"
              :years="birthYears"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-form-actions>
        <required-text />
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="onCancel"
        />
        <submit-button />
      </oxd-form-actions>
    </oxd-form>
  </div>
  <oxd-divider />
</template>

<script>
import {
  required,
  validDateFormat,
  shouldNotExceedCharLength,
} from '@ohrm/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';

const dependentModel = {
  name: '',
  relationshipType: null,
  relationship: '',
  dateOfBirth: '',
};

export default {
  name: 'EditDependent',

  props: {
    http: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
  },

  emits: ['close'],

  setup() {
    const {userDateFormat} = useDateFormat();

    return {
      userDateFormat,
    };
  },

  data() {
    return {
      isLoading: false,
      birthYears: Array.from(
        {length: new Date().getFullYear() - 1899},
        (_, index) => 1900 + index,
      ),
      dependent: {...dependentModel},
      rules: {
        name: [required, shouldNotExceedCharLength(100)],
        relationshipType: [required],
        relationship: [required, shouldNotExceedCharLength(100)],
        dateOfBirth: [validDateFormat(this.userDateFormat)],
      },
      relationshipOptions: [
        {id: 'spouse', label: 'Spouse'},
        {id: 'child', label: this.$t('pim.child')},
        {id: 'father', label: 'Father'},
        {id: 'mother', label: 'Mother'},
        {id: 'father_in_law', label: 'Father in Law'},
        {id: 'mother_in_law', label: 'Mother in Law'},
        {id: 'other', label: this.$t('pim.other')},
      ],
    };
  },

  computed: {
    showRelationship() {
      return this.dependent.relationshipType?.id == 'other';
    },
  },

  beforeMount() {
    this.isLoading = true;
    this.http
      .get(this.data.id)
      .then((response) => {
        const {data} = response.data;
        this.dependent = {...dependentModel, ...data};
        this.dependent.relationshipType = this.relationshipOptions.find(
          (item) => item.id === data.relationshipType,
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onSave() {
      this.isLoading = true;
      this.http
        .update(this.data.id, {
          ...this.dependent,
          relationshipType: this.dependent.relationshipType?.id,
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.dependent = {...dependentModel};
          this.onCancel();
        });
    },
    onCancel() {
      this.$emit('close', true);
    },
  },
};
</script>
